import { CHANNELS } from "../../shared/channels";
import { CharacterSheet, Doodle, DoodleDraft } from "../../shared/types";
import { call, isWeb, listen, mediaSrc } from "../bridge";
import { byId, flashToast, renderList } from "../dom";
import { canUse, myId, myName } from "../me";
import { isViewActive, onViewShown } from "../navigation";
import { debounce, matchesSearch, shrinkImage } from "../utils";
import { locale, t } from "../i18n";

// the doodle wall: paste or pick pictures, then say who drew them, when, and which characters are in them

let doodles: Doodle[] = [];
let sheets: CharacterSheet[] = [];
let author = "all";
let editing: Doodle | null = null;
let picked: string[] = [];
let shown: Doodle | null = null;

// small enough doodles go up as they are, so pngs keep their edges and gifs keep moving
const KEEP_BYTES = 6_000_000;
const KEEP_TYPES = new Set(["image/png", "image/jpeg", "image/webp", "image/gif"]);
const BIG_PX = 2400;
const THUMB_PX = 480;

function localDay(at = new Date()): string {
  return new Date(at.getTime() - at.getTimezoneOffset() * 60_000).toISOString().slice(0, 10);
}

function dayLabel(day: string): string {
  const [year, month, date] = day.split("-").map(Number);
  if (!year || !month || !date) return "";
  return new Date(year, month - 1, date).toLocaleDateString(locale(), { day: "numeric", month: "short", year: "numeric" });
}

function total(doodle: Doodle): number {
  return Object.values(doodle.counts).reduce((sum, count) => sum + count, 0);
}

function ocName(id: string): string {
  return sheets.find((sheet) => sheet.id === id)?.name || "";
}

function ocNames(doodle: Doodle): string[] {
  return doodle.ocs.map(ocName).filter(Boolean);
}

function status(text: string): void {
  byId("doodle-status").hidden = !text;
  byId("doodle-status-text").textContent = text;
}

function chip(text: string, onClick?: () => void): HTMLElement {
  const element = document.createElement(onClick ? "button" : "span");
  element.className = "chip";
  element.textContent = text;
  if (element instanceof HTMLButtonElement) element.type = "button";
  if (onClick) element.addEventListener("click", onClick);
  return element;
}

function visible(): Doodle[] {
  const search = byId<HTMLInputElement>("doodle-search").value;
  return doodles.filter((doodle) => {
    if (author !== "all" && (doodle.author || "unknown") !== author) return false;
    return matchesSearch(search, doodle.title, doodle.author, doodle.tags.join(" "), doodle.note, ocNames(doodle).join(" "));
  });
}

function paintAuthors(): void {
  const counts = new Map<string, number>();
  doodles.forEach((doodle) => counts.set(doodle.author || "unknown", (counts.get(doodle.author || "unknown") || 0) + 1));
  const host = byId("doodle-authors");
  host.hidden = counts.size < 2;
  host.replaceChildren(...["all", ...[...counts.keys()].sort()].map((name) => {
    const key = chip(name === "all" ? t("all {n}", { n: doodles.length }) : `${name === "unknown" ? t("unknown") : name} ${counts.get(name)}`, () => {
      author = name;
      render();
    });
    key.classList.toggle("active", name === author);
    return key;
  }));
}

function tile(doodle: Doodle): HTMLElement {
  const card = document.createElement("button");
  card.type = "button";
  card.className = "tile doodle-tile";
  const media = document.createElement("span");
  media.className = "tile-media";
  const image = document.createElement("img");
  image.alt = doodle.title;
  image.loading = "lazy";
  image.decoding = "async";
  image.src = mediaSrc(doodle.thumbUrl);
  media.append(image);
  const title = document.createElement("span");
  title.className = "tile-title";
  title.textContent = doodle.title || dayLabel(doodle.date) || t("doodle");
  const meta = document.createElement("span");
  meta.className = "tile-meta";
  meta.textContent = [doodle.author, ...ocNames(doodle)].filter(Boolean).join(" · ");
  card.append(media, title, meta);
  const faps = total(doodle);
  if (faps) {
    const count = document.createElement("span");
    count.className = "tile-count";
    count.textContent = `${faps}×`;
    count.title = t("fapped {n} times", { n: faps });
    card.append(count);
  }
  if (doodle.tags.includes("nsfw")) {
    const badge = document.createElement("span");
    badge.className = "tile-badge";
    badge.textContent = "nsfw";
    card.append(badge);
  }
  card.addEventListener("click", () => openViewer(doodle));
  return card;
}

function render(): void {
  if (author !== "all" && !doodles.some((doodle) => (doodle.author || "unknown") === author)) author = "all";
  const list = visible();
  renderList(byId("doodle-grid"), list, tile);
  byId("doodle-empty").hidden = list.length > 0;
  byId("doodle-empty").textContent = doodles.length ? t("no doodles match") : t("no doodles yet. paste one, or add pictures from your phone");
  byId("doodle-count").textContent = list.length === doodles.length ? `${doodles.length || ""}` : t("{shown} of {total}", { shown: list.length, total: doodles.length });
  paintAuthors();
}

function load(next: Doodle[]): void {
  doodles = next;
  render();
  if (shown) {
    const fresh = doodles.find((doodle) => doodle.id === shown?.id);
    if (fresh) openViewer(fresh);
    else closeViewer();
  }
}

async function reload(): Promise<void> {
  try {
    const [list, characters] = await Promise.all([
      call<Doodle[]>(CHANNELS.doodlesList),
      canUse("ocs") ? call<CharacterSheet[]>(CHANNELS.ocsList).catch(() => sheets) : Promise.resolve([])
    ]);
    sheets = [...characters].sort((a, b) => a.name.localeCompare(b.name));
    load(list);
  } catch (error) {
    flashToast(error instanceof Error ? error.message : t("could not load the doodles"));
  }
}

function readAsDataUrl(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error(t("could not read that picture")));
    reader.readAsDataURL(file);
  });
}

async function draftFor(file: File, pasted: boolean): Promise<DoodleDraft> {
  const keep = KEEP_TYPES.has(file.type) && file.size <= KEEP_BYTES;
  return {
    imageData: keep ? await readAsDataUrl(file) : await shrinkImage(file, BIG_PX),
    thumbData: await shrinkImage(file, THUMB_PX),
    author: myName(),
    // a picked file says when it was last saved, which is the closest thing to when it was drawn
    date: localDay(pasted || !file.lastModified ? new Date() : new Date(file.lastModified))
  };
}

async function addFiles(files: File[], pasted: boolean): Promise<void> {
  const images = files.filter((file) => file.type.startsWith("image/"));
  if (!images.length) return flashToast(pasted ? t("no picture on the clipboard") : t("pick a picture"));
  if (!canUse("doodles", "edit")) return flashToast(t("you can look at the doodles, not add them"));
  const before = new Set(doodles.map((doodle) => doodle.id));
  try {
    for (const [index, file] of images.entries()) {
      status(images.length === 1 ? t("adding the doodle") : t("adding {n} of {total}", { n: index + 1, total: images.length }));
      load(await call<Doodle[]>(CHANNELS.doodlesAdd, [await draftFor(file, pasted)]));
    }
    const added = doodles.filter((doodle) => !before.has(doodle.id));
    // one new doodle goes straight to its details, a pile of them is better tagged one by one
    if (added.length === 1) openEditor(added[0]);
    else flashToast(t("{n} doodles added", { n: added.length }));
  } catch (error) {
    flashToast(error instanceof Error ? error.message : t("could not add that"));
  } finally {
    status("");
  }
}

async function pasteFromClipboard(): Promise<void> {
  if (!navigator.clipboard?.read) return flashToast(t("press ctrl+v or ⌘v to paste"));
  try {
    const files: File[] = [];
    for (const item of await navigator.clipboard.read()) {
      const type = item.types.find((kind) => kind.startsWith("image/"));
      if (type) files.push(new File([await item.getType(type)], `pasted.${type.split("/")[1]}`, { type }));
    }
    await addFiles(files, true);
  } catch {
    flashToast(t("couldn't read the clipboard, try ctrl+v or ⌘v"));
  }
}

// --- editor

function paintPicked(): void {
  byId("doodle-oc-picked").replaceChildren(...picked.map((id) => {
    const key = chip(`${ocName(id) || t("gone")} ×`, () => {
      picked = picked.filter((item) => item !== id);
      paintPicked();
      paintOptions();
    });
    key.classList.add("active");
    key.setAttribute("aria-label", t("remove {name}", { name: ocName(id) }));
    return key;
  }));
}

function paintOptions(): void {
  const input = byId<HTMLInputElement>("doodle-oc-search");
  const search = input.value.trim();
  const options = search
    ? sheets.filter((sheet) => !picked.includes(sheet.id) && matchesSearch(search, sheet.name, sheet.aliases.join(" "))).slice(0, 8)
    : [];
  byId("doodle-oc-options").replaceChildren(...options.map((sheet) => chip(`+ ${sheet.name}`, () => {
    picked.push(sheet.id);
    input.value = "";
    paintPicked();
    paintOptions();
    input.focus();
  })));
}

function openEditor(doodle: Doodle): void {
  editing = doodle;
  picked = [...doodle.ocs];
  byId("doodle-form-title").textContent = doodle.title || t("new doodle");
  byId<HTMLImageElement>("doodle-form-preview").src = mediaSrc(doodle.thumbUrl);
  byId<HTMLInputElement>("doodle-title").value = doodle.title;
  byId<HTMLInputElement>("doodle-author").value = doodle.author;
  byId<HTMLInputElement>("doodle-date").value = doodle.date;
  byId<HTMLInputElement>("doodle-tags").value = doodle.tags.join(", ");
  byId<HTMLTextAreaElement>("doodle-note").value = doodle.note;
  byId<HTMLInputElement>("doodle-oc-search").value = "";
  const authors = [...new Set([myName(), ...doodles.map((item) => item.author)].filter(Boolean))];
  byId("doodle-author-list").replaceChildren(...authors.map((name) => Object.assign(document.createElement("option"), { value: name })));
  byId("doodle-ocs").hidden = !sheets.length;
  paintPicked();
  paintOptions();
  byId("doodle-editor").hidden = false;
}

function closeEditor(): void {
  editing = null;
  byId("doodle-editor").hidden = true;
}

async function saveEditor(): Promise<void> {
  if (!editing) return;
  try {
    load(await call<Doodle[]>(CHANNELS.doodlesSave, {
      id: editing.id,
      title: byId<HTMLInputElement>("doodle-title").value,
      author: byId<HTMLInputElement>("doodle-author").value,
      date: byId<HTMLInputElement>("doodle-date").value,
      ocs: picked,
      tags: byId<HTMLInputElement>("doodle-tags").value.split(","),
      note: byId<HTMLTextAreaElement>("doodle-note").value
    } satisfies DoodleDraft));
    closeEditor();
    flashToast(t("doodle saved"));
  } catch (error) {
    flashToast(error instanceof Error ? error.message : t("could not save"));
  }
}

async function deleteDoodle(): Promise<void> {
  if (!editing || !confirm(t("delete this doodle?"))) return;
  try {
    const id = editing.id;
    closeEditor();
    if (shown?.id === id) closeViewer();
    load(await call<Doodle[]>(CHANNELS.doodlesRemove, id));
  } catch (error) {
    flashToast(error instanceof Error ? error.message : t("could not delete"));
  }
}

// --- viewer

function openViewer(doodle: Doodle): void {
  shown = doodle;
  const url = mediaSrc(doodle.url);
  byId<HTMLImageElement>("doodle-viewer-image").src = url;
  byId("doodle-viewer-title").textContent = doodle.title || t("untitled");
  byId("doodle-viewer-by").textContent = [doodle.author && t("by {name}", { name: doodle.author }), dayLabel(doodle.date)].filter(Boolean).join(" · ") || t("doodle");
  // a character opens the wall filtered to them
  byId("doodle-viewer-ocs").replaceChildren(...ocNames(doodle).map((name) => chip(name, () => {
    byId<HTMLInputElement>("doodle-search").value = name;
    author = "all";
    closeViewer();
    render();
  })));
  byId("doodle-viewer-tags").replaceChildren(...doodle.tags.map((tag) => chip(`#${tag}`)));
  byId("doodle-viewer-note").textContent = doodle.note;
  paintCount(doodle);
  const download = byId<HTMLAnchorElement>("doodle-viewer-download");
  download.href = url;
  download.download = doodle.file;
  const list = visible();
  const at = list.findIndex((item) => item.id === doodle.id);
  byId<HTMLButtonElement>("doodle-viewer-prev").disabled = at <= 0;
  byId<HTMLButtonElement>("doodle-viewer-next").disabled = at < 0 || at >= list.length - 1;
  byId("doodle-viewer").hidden = false;
}

function paintCount(doodle: Doodle): void {
  const faps = total(doodle);
  const mine = doodle.counts[myId()] || 0;
  const done = doodle.countedOn[myId()] === localDay();
  const key = byId<HTMLButtonElement>("doodle-viewer-fap");
  key.disabled = done;
  key.title = done ? t("one a day per doodle, come back tomorrow") : t("count a fap");
  byId("doodle-viewer-fap-count").textContent = done ? t("today ✓ {n}", { n: faps }) : faps ? t("fap {n}", { n: faps }) : t("fap");
  byId("doodle-viewer-faps").hidden = !faps;
  byId("doodle-viewer-faps-text").textContent = t("fapped {n}×", { n: faps }) + (mine ? ` · ${t("you {n}", { n: mine })}` : "");
  byId("doodle-viewer-unfap").hidden = !mine;
}

async function count(delta: number): Promise<void> {
  if (!shown) return;
  try {
    load(await call<Doodle[]>(CHANNELS.doodlesCount, shown.id, delta, localDay()));
  } catch (error) {
    flashToast(error instanceof Error ? error.message : t("could not count that"));
  }
}

function closeViewer(): void {
  shown = null;
  byId("doodle-viewer").hidden = true;
}

function step(by: number): void {
  if (!shown) return;
  const list = visible();
  const next = list[list.findIndex((item) => item.id === shown?.id) + by];
  if (next) openViewer(next);
}

export function setupDoodlesView(): void {
  const files = byId<HTMLInputElement>("doodle-files");
  files.addEventListener("change", async () => {
    await addFiles([...(files.files || [])], false);
    files.value = "";
  });
  byId("doodle-paste").addEventListener("click", () => void pasteFromClipboard());
  byId("doodle-search").addEventListener("input", debounce(render, 120));

  // ctrl+v anywhere on the wall, as long as nobody is typing in a box
  document.addEventListener("paste", (event) => {
    if (!isViewActive("doodles") || !byId("doodle-editor").hidden) return;
    const pasted = [...(event.clipboardData?.files || [])].filter((file) => file.type.startsWith("image/"));
    if (!pasted.length) return;
    event.preventDefault();
    void addFiles(pasted, true);
  });
  const view = byId("view-doodles");
  view.addEventListener("dragover", (event) => {
    if (event.dataTransfer?.types.includes("Files")) event.preventDefault();
  });
  view.addEventListener("drop", (event) => {
    const dropped = [...(event.dataTransfer?.files || [])];
    if (!dropped.length) return;
    event.preventDefault();
    void addFiles(dropped, false);
  });

  byId("doodle-oc-search").addEventListener("input", paintOptions);
  byId("doodle-oc-search").addEventListener("keydown", (event) => {
    if (event.key !== "Enter") return;
    // enter takes the first match instead of saving the form
    event.preventDefault();
    byId("doodle-oc-options").querySelector<HTMLButtonElement>("button")?.click();
  });
  byId("doodle-form").addEventListener("submit", (event) => {
    event.preventDefault();
    void saveEditor();
  });
  byId("doodle-cancel").addEventListener("click", closeEditor);
  byId("doodle-delete").addEventListener("click", () => void deleteDoodle());

  byId("doodle-viewer-close").addEventListener("click", closeViewer);
  byId("doodle-viewer-prev").addEventListener("click", () => step(-1));
  byId("doodle-viewer-next").addEventListener("click", () => step(1));
  byId("doodle-viewer-fap").addEventListener("click", () => void count(1));
  byId("doodle-viewer-unfap").addEventListener("click", () => void count(-1));
  byId("doodle-viewer-edit").addEventListener("click", () => shown && openEditor(shown));
  byId("doodle-viewer-download").addEventListener("click", (event) => {
    if (isWeb()) return;
    event.preventDefault();
    void call(CHANNELS.openExternal, byId<HTMLAnchorElement>("doodle-viewer-download").href);
  });
  window.addEventListener("keydown", (event) => {
    if (!shown || !byId("doodle-editor").hidden) return;
    if (event.key === "Escape") closeViewer();
    if (event.key === "ArrowLeft") step(-1);
    if (event.key === "ArrowRight") step(1);
  });

  listen(CHANNELS.doodlesUpdated, (payload) => {
    if (Array.isArray(payload)) load(payload as Doodle[]);
  });
  listen(CHANNELS.ocsUpdated, (payload) => {
    if (!Array.isArray(payload)) return;
    sheets = [...(payload as CharacterSheet[])].sort((a, b) => a.name.localeCompare(b.name));
    render();
  });
  onViewShown("doodles", () => {
    view.querySelectorAll<HTMLElement>(".doodle-edit-key").forEach((key) => { key.hidden = !canUse("doodles", "edit"); });
    void reload();
  });
}
